import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Heading,
  Flex,
  Container,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Text,
} from '@chakra-ui/react';
import { withRouter } from 'react-router-dom';
import { Layout, ConfirmModal, EditSessionModal, InfoModal } from '../';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSessions,
  removeSession,
  setEditSession,
  SessionStatus,
  clearSessions,
  finishSession,
} from '../../redux/reducers/';
import { closeModal, openModal, createEmptySession, Modals } from '../../redux/reducers';
import SessionTable, { Mode } from './sessionTable/sessionTable';
import CreateSession from './createSession';
import { createEditableSession } from '../../utils/session/session';
import { InfoModalMode } from '../common/modal/infoModal';
import { isSupported } from 'twilio-video';

const PageStatuses = Object.freeze({
  Normal: 'normal',
  Editing: 'editing',
});

const INITIAL_FETCH_COUNT = 5;

const InterpreterPage = ({ history }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showModal, modalProps, modalName } = useSelector((state) => state.ui);
  const {
    sessions,
    overallSessionCount,
    fetchingSessions,
    sessionFetchError,
    sessionDeleteError,
    editSession,
  } = useSelector((state) => state.session);
  const userData = useSelector((state) => state.user.userData) || {};
  const [tabIndex, setTabIndex] = useState(0);
  const [pageStatus, setPageStatus] = useState(PageStatuses.Normal);

  useEffect(() => {
    console.log('use-effect fetch sessions');
    dispatch(fetchSessions(0, INITIAL_FETCH_COUNT - 1, [SessionStatus.NEW, SessionStatus.ONGOING]));
  }, []);

  useEffect(() => {
    if (sessionDeleteError != null) {
      dispatch(
        openModal(
          {
            headerText: t('modal.error'),
            bodyText: `${t('modal.removeSession.error')}: ${sessionDeleteError?.message}`,
          },
          Modals.ERROR
        )
      );
    }
  }, [sessionDeleteError]);

  const launchSession = (session) => {
    // open session page
    history.push(`/interpreter-session/${session.sessionId}`);
  };

  const onLoadSessions = (startIndex, stopIndex, mode) => {
    let status = [SessionStatus.NEW, SessionStatus.ONGOING];
    if (mode === Mode.Past) {
      status = [SessionStatus.FINISHED];
    }

    console.log('onLoadSessions', status);

    // load more
    dispatch(fetchSessions(startIndex, stopIndex, status));
  };

  const onChangeTab = (index) => {
    const sessionStatus =
      index === 0 ? [SessionStatus.NEW, SessionStatus.ONGOING] : [SessionStatus.FINISHED];

    // clear
    dispatch(clearSessions());

    console.log('onChangeTab', sessionStatus);

    // load
    dispatch(fetchSessions(0, INITIAL_FETCH_COUNT - 1, sessionStatus));

    // update index
    setTabIndex(index);
  };

  const renderTab = (mode) => {
    if (sessionFetchError != null) {
      const status = sessionFetchError.status || 500;
      return (
        <Text mt={4} color="red">
          {`${t('sessionFetchingFailed')} ${status}`}
        </Text>
      );
    }

    return (
      <SessionTable
        mt={4}
        mode={mode}
        sessions={sessions || []}
        overallSessionCount={overallSessionCount}
        onEditHandler={(session) => {
          // open edit modal
          const editableSession = createEditableSession(session);
          setPageStatus(PageStatuses.Editing);
          dispatch(openModal({}, Modals.EDIT_SESSION));
          dispatch(setEditSession(editableSession));
        }}
        onRemoveHandler={(session) => {
          // verify remove
          dispatch(
            openModal(
              {
                headerText: t('modal.removeSession.header'),
                bodyText: t('modal.removeSession.body'),
                leftButtonText: t('modal.cancel'),
                rightButtonText: t('modal.remove'),
                onRightButtonClick: () => {
                  dispatch(removeSession(session));
                  dispatch(closeModal());
                },
              },
              Modals.CONFIRM
            )
          );
        }}
        onSessionStartHandler={(session) => {
          if (isSupported) {
            // if ongoing start open directly
            if (session.status === SessionStatus.ONGOING) {
              launchSession(session);
            } else {
              // confirm
              dispatch(
                openModal(
                  {
                    headerText: t('modal.startSession.header'),
                    bodyText: t('modal.startSession.body'),
                    leftButtonText: t('modal.cancel'),
                    rightButtonText: t('modal.ok'),
                    onRightButtonClick: () => {
                      launchSession(session);
                      dispatch(closeModal());
                    },
                  },
                  Modals.CONFIRM
                )
              );
            }
          } else {
            dispatch(openModal({}, Modals.NOT_SUPPORTED));
          }
        }}
        onFinishHandler={(session) => {
          // verify finish
          dispatch(
            openModal(
              {
                headerText: t('modal.finishSession.header'),
                bodyText: t('modal.finishSession.body'),
                leftButtonText: t('modal.cancel'),
                rightButtonText: t('modal.ok'),
                onRightButtonClick: () => {
                  dispatch(finishSession(session));
                  dispatch(closeModal());
                },
              },
              Modals.CONFIRM
            )
          );
        }}
        loading={fetchingSessions}
        onLoad={(startIndex, stopIndex) => {
          if (fetchingSessions === false) {
            onLoadSessions(startIndex, stopIndex, mode);
          }
        }}
      />
    );
  };

  const doCloseEditSessionModal = () => {
    dispatch(setEditSession(null));
    setPageStatus(PageStatuses.Normal);
    dispatch(closeModal());
  };

  return (
    <Layout>
      <ConfirmModal
        open={
          showModal === true && pageStatus === PageStatuses.Normal && modalName === Modals.CONFIRM
        }
        bodyTextProps={{ mt: 2, mb: 10 }}
        onClose={() => dispatch(closeModal())}
        onLeftButtonClick={() => dispatch(closeModal())}
        {...modalProps}
      />
      <EditSessionModal
        open={
          showModal === true &&
          pageStatus === PageStatuses.Editing &&
          modalName === Modals.EDIT_SESSION
        }
        editSession={editSession}
        onClose={() => doCloseEditSessionModal()}
        onLeftButtonClick={() => doCloseEditSessionModal()}
      />
      <InfoModal
        open={showModal === true && modalName === Modals.NOT_SUPPORTED}
        mode={InfoModalMode.Error}
        bodyText={t('modal.browserNotSupported')}
        leftButtonText={null}
        onClose={() => doCloseEditSessionModal()}
        onRightButtonClick={() => doCloseEditSessionModal()}
      />
      <InfoModal
        open={showModal && modalName === Modals.ERROR}
        onRightButtonClick={() => dispatch(closeModal())}
        onClose={() => dispatch(closeModal())}
        leftButtonText={null}
        {...modalProps}
      />
      <Flex height="100%" pb="32" bg="brand.10">
        <Container>
          <Heading as="h3" my="8">
            {userData.username}
          </Heading>
          <Tabs
            tabIndex={tabIndex}
            onChange={(index) => onChangeTab(index)}
            variant="unstyled"
            mt="8"
            width="100%"
          >
            <TabList>
              <Tab
                _selected={{ borderBottom: 'solid 2px #BA494C' }}
                isDisabled={fetchingSessions === true}
              >
                <Trans i18nKey="interpreter.upcoming" />
              </Tab>
              <Tab
                _selected={{ borderBottom: 'solid 2px #BA494C' }}
                isDisabled={fetchingSessions === true}
              >
                <Trans i18nKey="interpreter.past" />
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel>{renderTab(Mode.Upcoming)}</TabPanel>
              <TabPanel>{renderTab(Mode.Past)}</TabPanel>
            </TabPanels>
          </Tabs>
        </Container>
        <Container>
          <CreateSession
            my={32}
            editSession={pageStatus === PageStatuses.Editing ? createEmptySession() : editSession}
          />
        </Container>
      </Flex>
    </Layout>
  );
};

InterpreterPage.propTypes = {
  history: PropTypes.object,
};

export default withRouter(InterpreterPage);
